'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  name: string;
  disableFallback?: boolean;
  fallback?: React.ReactElement;
  rescue?(): void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

interface ExtendedError extends Error {
  request?: string;
  type?: string;
  code?: string;
}

const EXCLUDED_ERROR_NAMES = ['ChunkLoadError'];
const EXCLUDED_ERROR_CODES = ['CSS_CHUNK_LOAD_FAILED'];

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.log('static ERROR BOUNDARY', error);
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ERRORED at KEY:', this.props.name, error, errorInfo);

    if (process.env.NODE_ENV === 'production') {
      this.logErrorToCoralogix(error, errorInfo);
    }

    if (this.props.rescue) {
      this.props.rescue();
    }
  }

  logErrorToCoralogix(error: Error, errorInfo: ErrorInfo): void {
    import('@coralogix/browser').then(module => {
      if (!this.isExcludedError(error)) {
        module.CoralogixRum.error('ERROR BOUNDARY:', { error, errorInfo });
      }
    });
  }

  isExcludedError(error: unknown): boolean {
    if (!error) return false;

    const err = error as ExtendedError;

    return !!(
      (err.name && EXCLUDED_ERROR_NAMES.includes(err.name)) ||
      (err.code && EXCLUDED_ERROR_CODES.includes(err.code))
    );
  }

  render(): ReactNode {
    const { children, disableFallback, fallback } = this.props;

    if (this.state.error) {
      //console.error('ERRORED at KEY:', name, this.state.error.toString());
      return disableFallback ? children : fallback || <h1>Something went wrong.</h1>;
    }

    return children;
  }
}
